import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { ApiResourceService } from 'libraries/service/apiResource/apiResource.service';
import { IGetEnumsAfidiumService, IGetEnumsAgency, IGetEnumsAgent, IGetEnumsService } from "./IGetEnums";
import { map, shareReplay, switchMap } from "rxjs/operators";
import { HostAliasResource, IHostCodesAndAliases } from "libraries/service/resources/host-alias/host-alias.resource";
import { ICodeName } from "libraries/service/resources/model/shared/ICodeName";
import { IQualityCheckDataDictionary, IQualityCheckDictionary } from "../IQualityCheck";
import { Yii } from "../../../interfaces/Yii";


@Injectable()
export class EnumsResourceService {
    private systems: Observable<IHostCodesAndAliases>;
    private types: Observable<string[]>;
    private services: Observable<IGetEnumsService[]>;
    private afidiumService: Observable<IGetEnumsAfidiumService[]>;
    private ticketingReportBookingCustomFields: Observable<ICodeName[]>;
    private readonly languageCode: string = Yii?.translate?.config?.language || 'fr';
    private qualityCheck: Observable<IQualityCheckDictionary>;
    private qualityCheckSection: Observable<IQualityCheckDataDictionary>;
    private actionForFareOptimisation: Observable<string[]>;

    public constructor(
        private readonly apiResourceService: ApiResourceService,
        private readonly hostAliasResource: HostAliasResource,
    ) {
    }

    public listSystems(): Observable<IHostCodesAndAliases> {
        if (!this.systems) {
            this.systems = this.apiResourceService.get<string>(
                this.apiResourceService.getApiUrl('booking', 'enums', 'host')
            )
                .pipe(
                    switchMap((hostCodes: string[]) => this.hostAliasResource.getCodesAndAliases(hostCodes)),
                    shareReplay()
                );
        }
        return this.systems;
    }

    //"contenu" field
    public listTypes(): Observable<string[]> {
        if (!this.types) {
            this.types = this.apiResourceService.get<string>(
                this.apiResourceService.getApiUrl('booking', 'enums', 'type')
            );
        }
        return this.types;
    }

    public listServices(): Observable<IGetEnumsService[]> {
        if (!this.services) {
            this.services = this.apiResourceService.get<IGetEnumsService>(
                this.apiResourceService.getApiUrl('booking', 'enums', 'service')
            )
                .pipe(
                    shareReplay()
                );
        }

        return this.services;
    }

    public listAgencies(): Observable<IGetEnumsAgency[]> {
        return this.apiResourceService.get<IGetEnumsAgency>(
            this.apiResourceService.getApiUrl('booking', 'enums', 'agency')
        )
            .pipe(
                map((response: IGetEnumsAgency[]): IGetEnumsAgency[] => {
                    return response.map((agency: IGetEnumsAgency): IGetEnumsAgency => {
                        if (!agency.code) {
                            return {
                                ...agency,
                                // @ts-ignore
                                code: `${agency.wCode}`
                            };
                        }
                        return {
                            ...agency,
                            code: `${agency.code}`
                        };
                    });
                }),

                shareReplay()
            );
    }

    public listAgents(): Observable<IGetEnumsAgent[]> {
        return this.apiResourceService.get<IGetEnumsAgent>(
            this.apiResourceService.getApiUrl('booking', 'enums', 'agent')
        )
            .pipe(
                map((response: IGetEnumsAgent[]): IGetEnumsAgent[] => {
                    return response.map((agent: IGetEnumsAgent): IGetEnumsAgent => {
                        return {
                            ...agent,
                            // @ts-ignore
                            name: `${agent.firstName} ${agent.lastName}`
                        };
                    });
                }),
                shareReplay()
            );
    }

    public listQualityCheck(): Observable<IQualityCheckDictionary> {
        if (!this.qualityCheck) {
            this.qualityCheck = this.apiResourceService.getOne<IQualityCheckDictionary>(
                this.apiResourceService.getApiUrl('booking', 'enums', 'qualityCheck'),
                {
                    languageCode: this.languageCode,
                }
            )
                .pipe(
                    shareReplay()
                );
        }
        return this.qualityCheck;
    }

    public listQualityCheckSection(): Observable<IQualityCheckDataDictionary> {
        if (!this.qualityCheckSection) {
            this.qualityCheckSection = this.apiResourceService.postOne<any, IQualityCheckDataDictionary>(
                this.apiResourceService.getApiUrl('dictionary', 'translations', 'dictionary', 'nameArray', 'search'),
                {
                    language: this.languageCode,
                    entityArray: [
                        "qualityCheckReason",
                        "qualityCheckKeyForAdditionalData",
                        "qualityCheckOperation",
                        "qualityCheckControl",
                        "qualityCheckAction"
                    ]
                }
            )
                .pipe(
                    shareReplay()
                );
        }
        return this.qualityCheckSection;
    }

    public listAfidiumServices(): Observable<IGetEnumsAfidiumService[]> {
        if (!this.afidiumService) {
            this.afidiumService = this.apiResourceService.get<IGetEnumsAfidiumService>(
                this.apiResourceService.getApiUrl('booking', 'enums', 'afidiumService')
            )
                .pipe(
                    shareReplay()
                );
        }

        return this.afidiumService;
    }

    public listTicketingReportBookingCustomFields(): Observable<ICodeName[]> {
        if (!this.ticketingReportBookingCustomFields) {
            this.ticketingReportBookingCustomFields = this.apiResourceService
                .get<ICodeName>(
                    this.apiResourceService.getApiUrl('tkt_report', 'search', 'enums', 'customFieldTranslated')
                )
                .pipe(
                    shareReplay()
                );
        }
        return this.ticketingReportBookingCustomFields;

    }

    public listActionForFareOptimisation(): Observable<string[]> {
        if (!this.actionForFareOptimisation) {
            this.actionForFareOptimisation = this.apiResourceService.get<string>(
                this.apiResourceService.getApiUrl('booking', 'enums', 'actionForFareOptimisation')
            );
        }
        return this.actionForFareOptimisation;
    }

    /* public export(request: BookingSearchParameters, format: ExportFormats): Observable<IApiExportResponse> {
         return this.apiResourceService.postOne<BookingSearchParameters, IApiExportResponse>(
             this.apiResourceService.getApiUrl('tkt_report', 'search', 'export', format),
             request
         );
     }*/
}
